import { useRef, useState } from "react";

/**
 * Tilting card frame with a moving foil sheen, used for parallel (foil) card
 * scans. Pointer position drives a small 3D rotation plus a specular highlight
 * so the card reflects like the physical parallel does. Purely presentational —
 * the underlying <img> is a normal lazy-loaded image, so crawlers still index it.
 */
export function FoilCard({
  src,
  alt,
  foil = true,
  loading = "lazy",
  className = "",
}: {
  src: string;
  alt: string;
  foil?: boolean;
  loading?: "eager" | "lazy";
  className?: string;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const [p, setP] = useState({ x: 50, y: 50, active: false });

  function move(clientX: number, clientY: number) {
    const el = ref.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    setP({ x: ((clientX - r.left) / r.width) * 100, y: ((clientY - r.top) / r.height) * 100, active: true });
  }

  const rx = (50 - p.y) / 6;
  const ry = (p.x - 50) / 6;

  return (
    <div
      ref={ref}
      className={`group relative overflow-hidden rounded-xl border border-border/60 bg-background/40 [perspective:900px] ${className}`}
      onPointerMove={(e) => move(e.clientX, e.clientY)}
      onPointerLeave={() => setP({ x: 50, y: 50, active: false })}
      onTouchMove={(e) => {
        const t = e.touches[0];
        if (t) move(t.clientX, t.clientY);
      }}
      onTouchEnd={() => setP({ x: 50, y: 50, active: false })}
      onTouchCancel={() => setP({ x: 50, y: 50, active: false })}
    >
      <img
        src={src}
        alt={alt}
        loading={loading}
        decoding="async"
        fetchPriority={loading === "eager" ? "high" : "low"}
        width={754}
        height={1053}
        className="block h-auto w-full transition-transform duration-150 will-change-transform"
        style={{ transform: p.active ? `rotateX(${rx}deg) rotateY(${ry}deg) scale(1.02)` : undefined }}
      />
      {foil ? (
        <>
          <span
            aria-hidden="true"
            className="pointer-events-none absolute inset-0 opacity-60 mix-blend-color-dodge transition-opacity duration-150"
            style={{
              opacity: p.active ? 0.85 : 0.35,
              background: `radial-gradient(circle at ${p.x}% ${p.y}%, rgba(255,255,255,0.55), rgba(120,200,255,0.25) 35%, rgba(255,120,220,0.18) 55%, transparent 72%)`,
            }}
          />
          <span
            aria-hidden="true"
            className="pointer-events-none absolute inset-0 mix-blend-overlay"
            style={{
              opacity: p.active ? 0.55 : 0.25,
              background: `linear-gradient(${100 + (p.x - 50) * 1.6}deg, transparent 20%, rgba(255,255,255,0.45) 46%, rgba(160,255,220,0.35) 52%, transparent 78%)`,
            }}
          />
        </>
      ) : null}
    </div>
  );
}
